import { useEffect } from 'react';

import { t } from '@/i18n';
import { QUICK_SEND_EVENT, reportQuickSend, takeQuickSend } from '@/lib/quickSend';
import { createChat, QuickSendError, sendQuick } from '@/services/quickSend';

/**
 * Listens for the Mac shell's quick-send and secret-chat shortcuts (lib/quickSend.ts) on every screen, makes the bridge
 * or the chat in the background and hands the link back to the shell. Draws nothing: the shell shows the result. Requests are sealed one
 * after another.
 */
export function QuickSendHost(): null {
  useEffect(() => {
    let queue: Promise<void> = Promise.resolve();
    const run = async (): Promise<void> => {
      const taken = await takeQuickSend();
      if (taken === null) return;
      if (taken.kind === 'chat') {
        try {
          reportQuickSend(taken.request, { link: await createChat() });
        } catch (cause) {
          reportQuickSend(taken.request, { problem: cause instanceof QuickSendError ? cause.message : t('quickSend.chatFailed') });
        }
        return;
      }
      const { request, content } = taken;
      if (content === null) {
        reportQuickSend(request, { problem: t('quickSend.nothingToSend') });
        return;
      }
      try {
        reportQuickSend(request, { link: await sendQuick(content) });
      } catch (cause) {
        reportQuickSend(request, { problem: cause instanceof QuickSendError ? cause.message : t('quickSend.bridgeFailed') });
      }
    };
    const onRequest = (): void => {
      queue = queue.then(run).catch(() => undefined);
    };
    onRequest();
    window.addEventListener(QUICK_SEND_EVENT, onRequest);
    return () => {
      window.removeEventListener(QUICK_SEND_EVENT, onRequest);
    };
  }, []);
  return null;
}
